import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  boolean,
  timestamp,
  primaryKey,
  mysqlEnum,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";

/**
 * Scoped RBAC model (reskin-docs/03). Entrust-style, ported as plain tables:
 * roles ← permission_role (per-role scope) → permissions ; role_user (user↔roles) ;
 * user_permissions (per-user overrides). The 5 scopes live as rows in permission_types
 * (added=1/owned=2/both=3/all=4/none=5 — seeded). Effective permission = most-permissive
 * scope across a user's roles + overrides, cached in Memcached, rebuilt when
 * users.permission_sync = 0.
 *
 * Ported line-by-line from the base migration: modules L1752–1757, permissions L1977–1987,
 * permission_types L1989–1993, roles L1130–1138, permission_role L1995–2003,
 * role_user L2500–2506, user_permissions L2943–2952.
 */

export const modules = mysqlTable("modules", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  moduleName: varchar("module_name", { length: 255 }).notNull(),
  description: varchar("description", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const permissions = mysqlTable("permissions", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull().unique(),
  displayName: varchar("display_name", { length: 255 }),
  description: varchar("description", { length: 255 }),
  moduleId: int("module_id", { unsigned: true })
    .notNull()
    .references(() => modules.id, { onDelete: "cascade", onUpdate: "cascade" }),
  isCustom: boolean("is_custom").default(false).notNull(),
  allowedPermissions: text("allowed_permissions"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const permissionTypes = mysqlTable("permission_types", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const roles = mysqlTable("roles", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull().unique(),
  displayName: varchar("display_name", { length: 255 }),
  description: varchar("description", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const permissionRole = mysqlTable(
  "permission_role",
  {
    permissionId: int("permission_id", { unsigned: true })
      .notNull()
      .references(() => permissions.id, { onDelete: "cascade", onUpdate: "cascade" }),
    roleId: int("role_id", { unsigned: true })
      .notNull()
      .references(() => roles.id, { onDelete: "cascade", onUpdate: "cascade" }),
    permissionTypeId: bigint("permission_type_id", { mode: "number", unsigned: true })
      .default(5) // 5 = NONE (reskin-docs/03 §1)
      .notNull()
      .references(() => permissionTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  },
  (t) => [primaryKey({ columns: [t.permissionId, t.roleId] })],
);

export const roleUser = mysqlTable(
  "role_user",
  {
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    roleId: int("role_id", { unsigned: true })
      .notNull()
      .references(() => roles.id, { onDelete: "cascade", onUpdate: "cascade" }),
  },
  (t) => [primaryKey({ columns: [t.userId, t.roleId] })],
);

/**
 * module_settings — per-company module on/off, per audience type (admin/employee/client).
 * The module-enablement gate (reskin-docs/02 §5): a pane is hidden + the API 403s when the
 * company's module row is `deactive`. Ported line-by-line from the base migration
 * 2018_01_01_000000_create_worksuite_new_table.php (module_settings block). No later alters.
 */
export const moduleSettings = mysqlTable("module_settings", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  moduleName: varchar("module_name", { length: 255 }).notNull(),
  status: mysqlEnum("status", ["active", "deactive"]).notNull(),
  type: mysqlEnum("type", ["admin", "employee", "client"]).default("admin").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const userPermissions = mysqlTable("user_permissions", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  permissionId: int("permission_id", { unsigned: true })
    .notNull()
    .references(() => permissions.id, { onDelete: "cascade", onUpdate: "cascade" }),
  permissionTypeId: bigint("permission_type_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => permissionTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
